import { useLayoutEffect, useRef } from "react";
import EmptyState from "@/components/EmptyState";
import Radio from "@/components/Radio";
import SectionHeading from "@/components/SectionHeading";
import type { ExperienceIndexCatalogCompetency } from "../queries";
import type { CareerPlanAssessmentAnswer } from "./careerPlanHistory";
import { getExperienceIndexAssessmentView } from "./experienceIndexAnswers";

export default function ExperienceIndexAnswersTab({
  answers,
  competencies = [],
  focusedCompetencyId
}: {
  answers: CareerPlanAssessmentAnswer[];
  competencies?: ExperienceIndexCatalogCompetency[];
  focusedCompetencyId?: string | null;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const emptyFocusRef = useRef<HTMLDivElement>(null);
  const wasFocusedRef = useRef(false);
  const groups = getExperienceIndexAssessmentView(competencies, answers, focusedCompetencyId);
  const focusedCompetencyName = groups[0]?.competencyName;
  const isFocused = Boolean(focusedCompetencyId);
  const isEmptyFocus = isFocused && groups.length === 0;

  useLayoutEffect(() => {
    if (isEmptyFocus) {
      emptyFocusRef.current?.focus();
      wasFocusedRef.current = true;
      return;
    }

    if (isFocused || wasFocusedRef.current) {
      headingRef.current?.focus();
    }

    wasFocusedRef.current = isFocused;
  }, [focusedCompetencyId, isEmptyFocus, isFocused]);

  if (answers.length === 0) {
    return (
      <EmptyState
        title="No Experience Index Answers"
        iconName="quiz"
        caption="This person has not answered Experience Index questions yet."
      />
    );
  }

  if (isEmptyFocus) {
    return (
      <div
        ref={emptyFocusRef}
        tabIndex={-1}
        role="region"
        aria-label="No Answers for This Competency"
        className="flex flex-col gap-6"
        data-testid="career-plan-ei-answers-tab"
      >
        <EmptyState
          title="No Answers for This Competency"
          iconName="quiz"
          caption="This person has not answered questions for this competency yet."
        />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="career-plan-ei-answers-tab"
      data-focused={isFocused ? "true" : undefined}
    >
      <SectionHeading
        headingRef={headingRef}
        heading={isFocused && focusedCompetencyName ? focusedCompetencyName : "Experience Index Answers"}
        body={
          isFocused
            ? "Each question lists every choice. The filled radio is this person's answer. You cannot change answers here."
            : "Each question lists every choice. The filled radio is this person's answer. Historical snapshots do not include answers."
        }
      />
      {groups.map((group) => (
        <div
          key={group.competencyId}
          className="flex flex-col gap-6"
          data-testid={`career-plan-ei-answers-${group.competencyId}`}
        >
          {isFocused ? null : (
            <p className="text-title-medium-bold font-serif text-sys-brand-on-surface">{group.competencyName}</p>
          )}
          <ol className="ml-4 list-decimal space-y-4 pl-5">
            {group.questions.map((question) => {
              const questionLabelId = `career-plan-ei-question-label-${question.questionId}`;

              return (
                <li
                  key={question.questionId}
                  className="text-title-medium"
                  data-testid={`career-plan-ei-question-${question.questionId}`}
                >
                  <div id={questionLabelId}>{question.questionContent}</div>
                  <ul className="list-none" role="radiogroup" aria-labelledby={questionLabelId}>
                    {question.options.map((option) => {
                      const optionLabelId = `career-plan-ei-option-label-${option.optionId}`;

                      return (
                        <li
                          key={option.optionId}
                          className="flex items-center my-4 z-0"
                          data-testid={`career-plan-ei-option-${option.optionId}`}
                          data-selected={option.selected ? "true" : undefined}
                        >
                          <Radio
                            label=""
                            checked={option.selected}
                            name={`career-plan-ei-question-${question.questionId}`}
                            id={`career-plan-ei-option-${option.optionId}`}
                            disabled={true}
                            aria-labelledby={optionLabelId}
                            onChange={() => {}}
                          />
                          <span id={optionLabelId} className="text-body-large">
                            {option.optionContent}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mb-10" />
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </div>
  );
}
